import { useState } from "react";
import { CloseCircleOutlined } from "@ant-design/icons";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSuspenseQuery } from "@tanstack/react-query";
import {
  Avatar,
  Button,
  Divider,
  Input,
  Modal,
  Space,
  Table,
  Typography,
} from "antd";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { projectQueries, UserInfo } from "@entities/project";
import { useCoordinator, useDisclosure } from "@shared/lib/hooks";
import { fallbackString } from "@shared/utils";
import { DocumentItem } from "../../../generic-document-modal";
import {
  AddRepresentativeSchema,
  useUploadRepresentative,
  useAddRepresentativeWithDocument,
} from "../../model";
import { RepresentationFormAdd } from "../representation-form-add";
import { RepresentationFormAuthor } from "../representation-form-author";
import { RepresentationFormEmail } from "../representation-form-email";
import "./representation-modal-orgs.scss";

const { Text } = Typography;

interface AddedEmployee {
  userInfo: UserInfo;
  formData: z.infer<typeof AddRepresentativeSchema>;
  activityName?: string;
  selectedDocumentId?: string;
}

interface RepresentativeModalOrgsProps {
  isOpen: boolean;
  handleClose: () => void;
  title?: string;
  type?: "settings" | "author";
  organization?: {
    representativeType:
      | "developer"
      | "technicalCustomer"
      | "buildingContractor"
      | "designerSupervisions"
      | "otherDevelopers"
      | string;
    isConstructionControl: boolean;
    companyId: string;
    projectParticipantId: string;
    userCompaniesHistoryId: string;
    developerType?: "developer" | "OPERATING" | "REGIONAL";
  };
  isLoading?: boolean;
  isArray?: boolean;
  error?: string;
  isControls?: boolean;
  isOtherDeveloper?: boolean;
  representativesAlreadyAdded?: any[];
  enableMultiAdd?: boolean;
  isAddFromOldService?: boolean;
}

type FormType = z.infer<typeof AddRepresentativeSchema>;

export const RepresentativeModalOrgs = ({
  isOpen,
  handleClose,
  title = "Добавление Уполномоченного",
  type,
  organization,
  isLoading,
  error,
  isArray,
  isControls,
  isOtherDeveloper,
  representativesAlreadyAdded,
  enableMultiAdd = false,
  isAddFromOldService,
}: RepresentativeModalOrgsProps) => {
  const { projectId, titleId } = useCoordinator();
  const { data: project } = useSuspenseQuery(
    projectQueries.getProject({ projectId }),
  );
  const {
    isOpen: isShowFormAdd,
    onOpen: onShowFormAdd,
    onClose: onCloseFormAdd,
  } = useDisclosure();
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [isSecondLoading, setIsSecondLoading] = useState(false);
  const [activityName, setActivityName] = useState("");
  const [selectedDocument, setSelectedDocument] = useState<DocumentItem | null>(
    null,
  );

  const [addedEmployees, setAddedEmployees] = useState<AddedEmployee[]>([]);
  const [clearTrigger, setClearTrigger] = useState(0);

  const handleIsShowFalse = () => {
    onCloseFormAdd();
    setUserInfo(null);
    setActivityName("");
  };

  const {
    control,
    handleSubmit,
    resetField,
    reset,
    setValue,
  } = useForm<FormType>({
    resolver: zodResolver(AddRepresentativeSchema),
    defaultValues: {
      projectSections: [{ name: "", number: "" }],
      type: type === "author" ? type : undefined,
    },
    shouldFocusError: false,
  });


  const { mutate } = useUploadRepresentative(
    isArray,
    isControls,
    isOtherDeveloper,
  );

  const { mutate: mutateWithDocument } = useAddRepresentativeWithDocument(
    isArray,
    isControls,
    isOtherDeveloper,
  );

  const handleOnCancel = () => {
    handleClose();
    setAddedEmployees([]);
    setUserInfo(null);
    onCloseFormAdd();
    setActivityName("");
    setClearTrigger(0);
  };

  const handleAddToList = (data: FormType) => {
    if (userInfo && enableMultiAdd) {
      const newEmployee: AddedEmployee = {
        userInfo,
        formData: data,
        activityName: isOtherDeveloper ? activityName : undefined,
        selectedDocumentId: selectedDocument?.key,
      };
      setAddedEmployees((prev) => [...prev, newEmployee]);
      setUserInfo(null);
      setActivityName("");
      setSelectedDocument(null);
      onCloseFormAdd();
      reset({
        name: "",
        number: "",
        issueDate: undefined,
        permit: undefined,
        organization: "",
        description: "",
        projectSections: type === "author" ? [{ name: "", number: "" }] : undefined,
        type: type === "author" ? type : undefined,
      });
      setClearTrigger((prev) => prev + 1);
    }
  };

  const handleRemoveFromList = (index: number) => {
    setAddedEmployees((prev) => prev.filter((_, i) => i !== index));
  };

  const handleClick = (data: FormType) => {
    if (organization) {
      const existingReps = representativesAlreadyAdded?.map((rep) => ({
        userCompaniesHistoryId: rep.userCompaniesHistoryId,
        userId: rep.user.userId,
        administrativeDocumentId:
          rep.administrativeDocument.administrativeDocumentId,
      }));
      setIsSecondLoading(true);

      if (isAddFromOldService && selectedDocument) {
        // @ts-expect-error поля формы не совпадают с типом IRequest мутации
        mutateWithDocument({
          projectId: project.externalId,
          titleId,
          ...data,
          administrativeDocumentId: selectedDocument.key,
          userId: userInfo?.userId ?? "",
          representative: {
            ...organization,
            ...(isOtherDeveloper ? { activityName } : {}),
          },
          existingRepresentatives: existingReps,
          onSuccess: () => {
            handleClose();
            setIsSecondLoading(false);
            setAddedEmployees([]);
            setUserInfo(null);
            onCloseFormAdd();
            setActivityName("");
            setSelectedDocument(null);
            reset();
          },
          setIsSecondLoading,
        });
      } else {
        // @ts-expect-error поля формы не совпадают с типом IRequest мутации
        mutate({
          projectId: project.externalId,
          titleId,
          ...data,
          userId: userInfo?.userId ?? "",
          representative: {
            ...organization,
            ...(isOtherDeveloper ? { activityName } : {}),
          },
          existingRepresentatives: existingReps,
          onSuccess: () => {
            handleClose();
            setIsSecondLoading(false);
            setAddedEmployees([]);
            setUserInfo(null);
            onCloseFormAdd();
            setActivityName("");
            reset();
          },
          setIsSecondLoading,
        });
      }
    }
  };

  const handleAddAll = async (data?: FormType) => {
    if (organization && enableMultiAdd) {
      setIsSecondLoading(true);

      const existingReps = representativesAlreadyAdded?.map((rep) => ({
        userCompaniesHistoryId: rep.userCompaniesHistoryId,
        userId: rep.user.userId,
        administrativeDocumentId:
          rep.administrativeDocument.administrativeDocumentId,
      }));

      const allEmployeesToAdd: AddedEmployee[] = [...addedEmployees];
      
      if (userInfo && data) {
        allEmployeesToAdd.push({
          userInfo,
          formData: data,
          activityName: isOtherDeveloper ? activityName : undefined,
          selectedDocumentId: selectedDocument?.key,
        });
      }

      if (allEmployeesToAdd.length === 0) {
        setIsSecondLoading(false);
        return;
      }

      allEmployeesToAdd.forEach((employee, index) => {
        if (isAddFromOldService && employee.selectedDocumentId) {
          // @ts-expect-error поля формы не совпадают с типом IRequest мутации
          mutateWithDocument({
            projectId: project.externalId,
            titleId,
            ...employee.formData,
            administrativeDocumentId: employee.selectedDocumentId,
            userId: employee.userInfo.userId ?? "",
            representative: {
              ...organization,
              ...(isOtherDeveloper
                ? { activityName: employee.activityName }
                : {}),
            },
            existingRepresentatives: existingReps,
            onSuccess: () => {
              if (index === allEmployeesToAdd.length - 1) {
                handleClose();
                setIsSecondLoading(false);
                setAddedEmployees([]);
                setUserInfo(null);
                onCloseFormAdd();
                setActivityName("");
                setSelectedDocument(null);
                reset();
              }
            },
            setIsSecondLoading,
          });
        } else {
          // @ts-expect-error поля формы не совпадают с типом IRequest мутации
          mutate({
            projectId: project.externalId,
            titleId,
            ...employee.formData,
            userId: employee.userInfo.userId ?? "",
            representative: {
              ...organization,
              ...(isOtherDeveloper
                ? { activityName: employee.activityName }
                : {}),
            },
            existingRepresentatives: existingReps,
            onSuccess: () => {
              if (index === allEmployeesToAdd.length - 1) {
                handleClose();
                setIsSecondLoading(false);
                setAddedEmployees([]);
                setUserInfo(null);
                onCloseFormAdd();
                setActivityName("");
                reset();
              }
            },
            setIsSecondLoading,
          });
        }
      });
    }
  };

  const onUserSelect = (user: UserInfo) => {
    if (user) {
      setUserInfo(user);
      onShowFormAdd();
    }
  };

  const projectParticipantId = organization?.projectParticipantId;

  const data = userInfo
    ? [
        {
          key: userInfo.userId,
          name: fallbackString(
            `${userInfo.userSurname ?? ""} ${userInfo.userFirstName ?? ""} ${userInfo.userMiddleName ?? ""}`,
          ),
          organization: fallbackString(userInfo.userCompanies?.[0]?.shortName),
          position: fallbackString(userInfo.userCompanies?.[0]?.position),
          initials: fallbackString(
            `${userInfo.userSurname?.[0] ?? ""}${userInfo.userFirstName?.[0] ?? ""}`.toUpperCase(),
          ),
          email: fallbackString(userInfo.userEmail),
          phone: fallbackString(userInfo.userMobilePhone),
          role: fallbackString(),
          nostroyId: fallbackString(),
        },
      ]
    : [];

  const addedEmployeesData = addedEmployees.map((employee, index) => ({
    key: `${employee.userInfo.userId}-${index}`,
    name: fallbackString(
      `${employee.userInfo.userSurname ?? ""} ${employee.userInfo.userFirstName ?? ""} ${employee.userInfo.userMiddleName ?? ""}`,
    ),
    organization: fallbackString(
      employee.userInfo.userCompanies?.[0]?.shortName,
    ),
    position: fallbackString(employee.userInfo.userCompanies?.[0]?.position),
    initials: fallbackString(
      `${employee.userInfo.userSurname?.[0] ?? ""}${employee.userInfo.userFirstName?.[0] ?? ""}`.toUpperCase(),
    ),
    index,
  }));

  const columns = [
    {
      title: "ФИО",
      dataIndex: "name",
      key: "name",
      render: (name: string, record: any) => (
        <Space size="middle">
          <Avatar>{record.initials}</Avatar>
          <Text>{name}</Text>
        </Space>
      ),
    },
    {
      title: "Организация",
      dataIndex: "organization",
      key: "organization",
      render: (organization: string) => <Text>{organization}</Text>,
    },
    {
      title: "Должность",
      dataIndex: "position",
      key: "position",
      render: (position: string) => <Text>{position}</Text>,
    },
    {
      key: "action",
      render: (_: any, record: any) => (
        <Button
          type="text"
          icon={
            <CloseCircleOutlined style={{ color: "red", fontSize: "16px" }} />
          }
          danger
          style={{ padding: 0 }}
          onClick={() => {
            if (enableMultiAdd && record.index !== undefined) {
              handleRemoveFromList(record.index);
            } else {
              handleIsShowFalse();
            }
          }}
        />
      ),
    },
  ];

  return (
    <>
      <Modal
        open={isOpen}
        onCancel={handleOnCancel}
        footer={null}
        centered
        width={900}
        title={title}
      >
        <div className="representation-orgs-description">
          Поиск сотрудника по адресу электронной почты
        </div>

        <RepresentationFormEmail
          projectParticipantId={projectParticipantId}
          onUserSelect={onUserSelect}
          error={error}
          clearTrigger={clearTrigger}
        />

        {userInfo && !enableMultiAdd ? (
          <div className="rep-author-modal">
            <Table
              dataSource={data}
              columns={columns}
              pagination={false}
              showHeader
              bordered={false}
              style={{ width: "100%" }}
            />
          </div>
        ) : null}

        {userInfo && enableMultiAdd ? (
          <div className="rep-author-modal rep-author-modal--spaced">
            <Table
              dataSource={data}
              columns={columns}
              pagination={false}
              showHeader
              bordered={false}
              style={{ width: "100%" }}
            />
          </div>
        ) : null}

        {isShowFormAdd && type !== "settings" ? (
          <RepresentationFormAdd
            control={control}
            resetField={resetField}
            setValue={setValue}
            isAddFromOldService={isAddFromOldService}
            onDocumentFromOldServiceSelect={setSelectedDocument}
          />
        ) : null}

        {isShowFormAdd && type === "author" ? (
          <RepresentationFormAuthor
            control={control}
            key={String(isShowFormAdd)}
          />
        ) : null}
        {isShowFormAdd && isOtherDeveloper ? (
          <div className="representation-orgs-activity-field">
            <div>Выполняемые работы</div>
            <Input
              value={activityName}
              onChange={(e) => setActivityName(e.target.value)}
            />
          </div>
        ) : null}

        {enableMultiAdd && userInfo && (
          <div className="representation-orgs-add-more">
            <Button
              type="link"
              style={{ padding: 0 }}
              onClick={() => {
                if (type === "settings") {
                  const emptyFormData: FormType = {
                    name: "",
                    number: "",
                    issueDate: undefined as any,
                    permit: undefined as any,
                    organization: "",
                    description: "",
                  };
                  handleAddToList(emptyFormData);
                } else {
                  handleSubmit(handleAddToList)();
                }
              }}
            >
              + Добавить ещё сотрудника
            </Button>
          </div>
        )}

        {enableMultiAdd && addedEmployees.length > 0 && (
          <>
            <Divider />
            <div className="representation-orgs-added-title">
              <Typography.Title level={5}>
                Добавленные сотрудники
              </Typography.Title>
            </div>
            <div className="rep-author-modal">
              <Table
                dataSource={addedEmployeesData}
                columns={columns}
                pagination={false}
                showHeader
                bordered={false}
                style={{ width: "100%" }}
              />
            </div>
          </>
        )}

        <Divider />

        {enableMultiAdd && addedEmployees.length > 0 ? (
          <Button
            type="primary"
            disabled={addedEmployees.length === 0 && !userInfo}
            loading={isLoading || isSecondLoading}
            onClick={() => {
              if (userInfo && isShowFormAdd) {
                handleSubmit(handleAddAll)();
              } else {
                handleAddAll();
              }
            }}
          >
            Добавить всех
          </Button>
        ) : (
          <Button
            type="primary"
            disabled={!userInfo}
            loading={isLoading || isSecondLoading}
            onClick={handleSubmit(handleClick)}
          >
            Добавить
          </Button>
        )}
      </Modal>
    </>
  );
};
